import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { useSolana } from '../context/WalletContext';
import { SEOHead } from '../components/SEOHead';
import { toast } from 'sonner';
import axios from 'axios';
import bs58 from 'bs58';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setAuth, user } = useAuth();
  const { settings: siteSettings } = useSite();
  const { connected, publicKey, signMessage, disconnect } = useSolana();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('connect'); // 'connect', 'signing', 'success', 'deactivated'
  const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false);
  const [deactivationMessage, setDeactivationMessage] = useState(null);
  const isLoginInProgress = useRef(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/home');
    }
  }, [user, navigate]);

  // Auto-trigger login when wallet connects (only once per session)
  useEffect(() => {
    if (connected && publicKey && step === 'connect' && !hasAttemptedLogin && !isLoginInProgress.current) {
      handleWalletLogin();
    }
  }, [connected, publicKey, step, hasAttemptedLogin]);

  const handleConnect = async () => {
    setError(null);
    setHasAttemptedLogin(false); // Reset flag when user manually tries to connect
  };

  const handleWalletLogin = async () => {
    if (!publicKey || isLoginInProgress.current) return;
    
    isLoginInProgress.current = true;
    setLoading(true);
    setStep('signing');
    setError(null);
    setHasAttemptedLogin(true);
    setDeactivationMessage(null);
    
    try {
      const walletAddress = publicKey.toBase58();
      
      // Step 0: Check account status first
      const statusRes = await axios.get(`${API}/auth/check-account-status?wallet_address=${walletAddress}`);
      
      if (statusRes.data.exists && !statusRes.data.is_active) {
        // Account is deactivated
        setStep('deactivated');
        setDeactivationMessage(statusRes.data.message || 'Your account has been deactivated by admin');
        setLoading(false);
        isLoginInProgress.current = false;
        disconnect();
        return;
      }
      
      // Step 1: Get nonce from backend
      const nonceRes = await axios.post(`${API}/auth/wallet/nonce`, {
        wallet_address: walletAddress
      });
      
      const { nonce, message } = nonceRes.data;
      
      // Step 2: Sign the message with Phantom
      const encodedMessage = new TextEncoder().encode(message);
      const signedMessage = await signMessage(encodedMessage);
      const signature = bs58.encode(signedMessage);
      
      // Step 3: Send signature to backend for verification
      const loginRes = await axios.post(`${API}/auth/wallet/login`, {
        wallet_address: walletAddress,
        signature: signature,
        nonce: nonce
      });
      
      // Step 4: Login successful
      const { access_token, user: userData } = loginRes.data;
      setAuth(access_token, userData);
      
      setStep('success');
      toast.success(t('messages.welcome'));
      
      setTimeout(() => {
        navigate('/home');
      }, 1000);
      
    } catch (err) {
      console.error('Wallet login error:', err);
      isLoginInProgress.current = false;
      
      // Check if account is deactivated
      if (err.response?.status === 403 && err.response?.data?.detail?.code === 'ACCOUNT_DEACTIVATED') {
        setStep('deactivated');
        setDeactivationMessage(err.response.data.detail.message || 'Your account has been deactivated');
        disconnect();
        return;
      }
      
      setStep('error');
      
      if (err.message?.includes('User rejected')) {
        setError(t('auth.signatureRejected', { defaultValue: 'You rejected the signature request.' }));
      } else if (typeof err.response?.data?.detail === 'string') {
        setError(err.response.data.detail);
      } else {
        setError(t('auth.verifyFailed', { defaultValue: 'Failed to verify wallet. Please try again.' }));
      }
    } finally {
      setLoading(false);
      isLoginInProgress.current = false;
    }
  };

  const primaryColor = siteSettings?.colors?.primary || '#FF8C00';
  const backgroundColor = '#06212E';

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden"
      style={{ backgroundColor }}
    >
      {/* SEO Meta Tags */}
      <SEOHead page="login" />
      
      {/* Back Button - Fixed at top */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate('/home')}
        className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20 hover:bg-white/20 transition-colors z-10"
        data-testid="login-back-button"
      >
        <ArrowLeft className="w-5 h-5 text-white" />
      </motion.button>
      
      {/* Background effects */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 30%, ${primaryColor}15, transparent 50%)` }}
      />
      
      <motion.div
        className="w-full max-w-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          {siteSettings?.logo ? (
            <img 
              src={siteSettings.logo} 
              alt={siteSettings?.app_name || 'Logo'}
              className="h-16 object-contain mx-auto mb-4"
            />
          ) : (
            <div className="flex flex-col items-center gap-3 mb-4">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ 
                  background: `linear-gradient(to bottom right, ${siteSettings?.colors?.accent || '#FFC300'}, ${primaryColor})`,
                  boxShadow: `0 20px 40px -10px ${primaryColor}50`
                }}
              >
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              {siteSettings?.app_name ? (
                <h1 className="text-2xl font-bold text-white font-['Chakra_Petch'] uppercase tracking-wider">
                  {siteSettings.app_name}
                </h1>
              ) : (
                <div className="h-8 w-32 bg-white/10 rounded animate-pulse"></div>
              )}
            </div>
          )}
          <p className="text-zinc-500 text-sm">
            {t('auth.connectToContinue', { defaultValue: 'Connect your Solana wallet to continue' })}
          </p>
        </div>

        {/* Login Card */}
        <div 
          className="rounded-2xl p-6 border"
          style={{ 
            backgroundColor: siteSettings?.colors?.card || '#18181b',
            borderColor: siteSettings?.colors?.card_border || '#27272a'
          }}
        >
          {step === 'deactivated' ? (
            <motion.div 
              className="text-center py-6"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-red-500/20">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{t('auth.accountDeactivated', { defaultValue: 'Account Deactivated' })}</h2>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
                <p className="text-red-300 text-sm">{deactivationMessage}</p>
              </div>
              <p className="text-zinc-500 text-sm">
                {t('auth.contactSupport', { defaultValue: 'Please contact support if you believe this is a mistake.' })}
              </p>
              <Button
                onClick={() => {
                  setStep('connect');
                  setDeactivationMessage(null);
                  setHasAttemptedLogin(false);
                }}
                variant="outline"
                className="mt-4 border-zinc-700 text-zinc-400"
              >
                {t('auth.tryAnotherWallet', { defaultValue: 'Try Another Wallet' })}
              </Button>
            </motion.div>
          ) : step === 'success' ? (
            <motion.div 
              className="text-center py-8"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <Sparkles className="w-8 h-8" style={{ color: primaryColor }} />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{t('messages.welcome')}</h2>
              <p className="text-zinc-400 text-sm">{t('auth.redirecting', { defaultValue: 'Redirecting to dashboard...' })}</p>
            </motion.div>
          ) : (
            <>
              {/* Error Message */}
              {error && (
                <motion.div 
                  className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-500/50 flex items-start gap-2"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </motion.div>
              )}

              {/* Single Button Logic */}
              {!connected ? (
                // Not connected - Show Select Wallet
                <div className="wallet-adapter-button-wrapper w-full" onClick={handleConnect}>
                  <WalletMultiButton 
                    className="!w-full !h-14 !text-lg !font-semibold !rounded-xl !transition-all !justify-center"
                    style={{ 
                      background: `linear-gradient(to right, ${primaryColor}, ${siteSettings?.colors?.primary_hover || '#0ea5e9'})`,
                      boxShadow: `0 10px 30px -5px ${primaryColor}50`
                    }}
                  />
                </div>
              ) : step === 'error' || error ? (
                // Connected but error - Show Try Again button only
                <Button
                  onClick={() => {
                    setError(null);
                    setStep('connect');
                    setHasAttemptedLogin(false);
                    handleWalletLogin();
                  }}
                  className="w-full h-14 text-lg font-semibold rounded-xl"
                  style={{ 
                    background: `linear-gradient(to right, ${primaryColor}, ${siteSettings?.colors?.primary_hover || '#0ea5e9'})`,
                    boxShadow: `0 10px 30px -5px ${primaryColor}50`
                  }}
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  {t('common.retry')}
                </Button>
              ) : (
                // Connected and signing in progress - Show loading only
                <div className="flex flex-col items-center justify-center py-6 gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
                  <p className="text-zinc-400 text-sm">
                    {step === 'signing' ? t('auth.signInWallet', { defaultValue: 'Please sign in your wallet...' }) : t('auth.verifying', { defaultValue: 'Verifying...' })}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
